// src/views/administration/finance/finance-officer-requests/FinanceRequestPreviewDialog.js
/* eslint-disable */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Box,
    Grid,
    Dialog,
    Button
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    CalendarMonth as CalendarIcon,
    AccountBalance as AccountBalanceIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    Article as ArticleIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { theme } from '../../../ConstantColors';
import TableCollapsible from 'views/trip/daily-exp/DailyExpensesCollapsibleTable';
import { useKeycloak } from '@react-keycloak/web';
import { InfoSection } from 'ui-component/commons/InfoSection';
import { LabelValue } from 'ui-component/commons/LabelValue';
import { DocumentSection } from 'views/documents/DocumentSection';
import useFetchDocuments from 'hooks/api/documents/useFetchDocuments';
import { formatDate, formatCurrency } from 'utils/formatters';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';

const FinanceRequestPreviewDialog = ({ open, onClose, previewData }) => {
    const { keycloak } = useKeycloak();
    const { getLineActivities, lineActivities } = useTravelRequest();

    const { documents } = useFetchDocuments({
        travelPlanId: previewData?.travelPlan?.id,
        token: keycloak?.token
    });

    useEffect(() => {
        if (previewData?.id) {
            getLineActivities(previewData.id);
        }
    }, [previewData?.id, getLineActivities]);

    if (!previewData) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: theme.primary.main,
                    color: 'white',
                    py: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Travel Request - {previewData.sequenceNumber} - {previewData.travelPlan?.planName}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                        Created: {formatDate(previewData.createdAt)}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
                {/* Request Overview */}
                <InfoSection title="Request Details" icon={ArticleIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Reference Number" value={previewData.sequenceNumber} chip />
                            <LabelValue label="Status" value={previewData.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Requester Information */}
                <InfoSection title="Requester Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${previewData.requestUser?.firstName} ${previewData.requestUser?.lastName}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email Address" value={previewData.requestUser?.email} icon={EmailIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Employee Number" value={previewData.requestUser?.employeeNumber} icon={BadgeIcon} />
                            <LabelValue label="Status" value={previewData.requestUser?.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Traveller Information */}
                <InfoSection title="Traveller Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${previewData.user?.firstName} ${previewData.user?.lastName}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email Address" value={previewData.user?.email} icon={EmailIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Employee Number" value={previewData.user?.employeeNumber} icon={BadgeIcon} />
                            <LabelValue label="Status" value={previewData.user?.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Travel Plan Details */}
                <InfoSection title="Travel Plan Details" icon={CalendarIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Travel Plan" value={previewData.travelPlan?.planName} icon={ArticleIcon} />
                            <LabelValue 
                                label="Travel Type" 
                                value={previewData.travelPlan?.type.replace(/_/g, ' ')} 
                                chip 
                            />
                            {previewData.travelPlan?.type && (
                                <LabelValue
                                    label="Lunch Status"
                                    value={
                                        previewData.travelPlan.type.includes('LUNCH_INCLUDED')
                                            ? 'Lunch will be provided at venue (not claimable)'
                                            : 'Lunch is not provided (claimable)'
                                    }
                                    chip
                                    chipProps={{
                                        color: previewData.travelPlan.type.includes('LUNCH_INCLUDED') ? 'info' : 'warning'
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Venue" value={previewData.travelPlan?.site?.name} chip />
                            <LabelValue
                                label="Location"
                                value={`${previewData.travelPlan?.site?.district?.name}, ${previewData.travelPlan?.site?.district?.province?.name}`}
                                icon={LocationIcon}
                            />
                        </Grid>

                        {/* Documents section */}
                        <Grid item xs={12} md={6}>
                            <DocumentSection documents={documents} theme={theme} keycloak={keycloak} />
                        </Grid>

                        <Grid item xs={12}>
                            <LabelValue
                                label="Travel Duration"
                                value={`From: ${formatDate(previewData.departureDatetime)} To: ${formatDate(
                                    previewData.returnDatetime
                                )}`}
                                icon={AccessTimeIcon}
                            />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Budget Details */}
                <InfoSection title="Budget Information" icon={AccountBalanceIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Budget Line"
                                value={`${previewData.budgetLine?.code}-${previewData.budgetLine?.description}`}
                                chip
                            />
                            <LabelValue
                                label="Branch Code"
                                value={`${previewData.branchCode?.code} - ${previewData.branchCode?.description}`}
                                icon={BusinessIcon}
                            />
                            <LabelValue
                                label="Project Code"
                                value={`${previewData.budgetLine?.projectCode?.code} - ${previewData.budgetLine?.projectCode?.description}`}
                                icon={BusinessIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Budget Amount" value={formatCurrency(previewData.budgetAmount)} chip />
                            <LabelValue label="Fiscal Year" value={previewData.budgetLine?.fiscalYear} icon={CalendarIcon} />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Daily Activities */}
                <InfoSection title="Daily Activities" icon={CalendarIcon}>
                    <TableCollapsible lineActivities={lineActivities || []} />
                </InfoSection>
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: theme.background.light, borderTop: `1px solid ${theme.primary.light}` }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        color: theme.text.secondary,
                        borderColor: theme.text.secondary,
                        '&:hover': {
                            borderColor: theme.text.primary,
                            color: theme.text.primary,
                            bgcolor: 'transparent'
                        }
                    }}
                    startIcon={<CloseIcon />}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

FinanceRequestPreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    previewData: PropTypes.shape({
        id: PropTypes.string.isRequired,
        sequenceNumber: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        createdAt: PropTypes.string,
        lastModifiedAt: PropTypes.string,
        createdBy: PropTypes.string,
        departureDatetime: PropTypes.string,
        returnDatetime: PropTypes.string,
        budgetAmount: PropTypes.number,
        requestUser: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            email: PropTypes.string,
            employeeNumber: PropTypes.string,
            status: PropTypes.string
        }),
        user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            email: PropTypes.string,
            employeeNumber: PropTypes.string,
            status: PropTypes.string
        }),
        travelPlan: PropTypes.shape({
            id: PropTypes.string.isRequired,
            planName: PropTypes.string,
            type: PropTypes.string,
            site: PropTypes.shape({
                name: PropTypes.string,
                district: PropTypes.shape({
                    name: PropTypes.string,
                    province: PropTypes.shape({
                        name: PropTypes.string
                    })
                })
            })
        }),
        budgetLine: PropTypes.shape({
            code: PropTypes.string,
            description: PropTypes.string,
            fiscalYear: PropTypes.string,
            projectCode: PropTypes.shape({
                code: PropTypes.string,
                description: PropTypes.string
            })
        }),
        branchCode: PropTypes.shape({
            code: PropTypes.string,
            description: PropTypes.string
        })
    })
};

export default FinanceRequestPreviewDialog; 